import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { CatalogMoldSize } from "../../../models/CatalogMoldSize";
import { useMoldSizeOptionsFunctions } from "../hooks/useMoldSizeOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { MoldSizeModal } from "./MoldSizeModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogMoldSizesFromAPI: Function;
  isLoading: boolean;
  moldSizeId: String | undefined;
};

export const TableMoldSize = ({
  getCatalogMoldSizesFromAPI,
  isLoading,
  moldSizeId,
}: Props) => {
  const navigate = useNavigate();

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [moldSize, setMoldSize] = useState<CatalogMoldSize | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewMoldSize = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setMoldSize(null);
    navigate(`/configuration/caster-mold-sizes/new`);
  };

  const { getMenuOptions } = useMoldSizeOptionsFunctions({
    setMoldSize,
    setShowModal,
    handleNewMoldSize,
  });
  const { rows, columnDefs } = useTableData({
    setMoldSize,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("edit");
      setMoldSize(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setMoldSize(event.data);
      setShowModal("edit");
      navigate(
        `/configuration/caster-mold-sizes/${
          (event.data as CatalogMoldSize).MoldSizeId
        }`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/caster-mold-sizes/${
          (event.data as CatalogMoldSize).MoldSizeId
        }`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "moldSizeContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setMoldSize(null);
    navigate(`/configuration/caster-mold-sizes/`);
  };

  useEffect(() => {
    if (moldSizeId) {
      if (moldSizeId === "new") {
        handleNewMoldSize();
      } else {
        let moldSizeNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogMoldSize).MoldSizeId === Number(moldSizeId)) {
            moldSizeNode = n;
            return true;
          }
        });

        if (moldSizeNode!) {
          const pageSize = gridApi?.paginationGetPageSize();
          const pageNumber = Math.floor(
            (moldSizeNode!.rowIndex ?? 0) / pageSize!,
          );
          gridApi?.paginationGoToPage(pageNumber);

          moldSizeNode.setSelected(true);
          setMoldSize(moldSizeNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "moldSizeContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography
            variant="h5"
            style={{ margin: "1px 0 10px", fontWeight: 600 }}
          >
            Mold Sizes
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogMoldSize>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              onGridReady={(params) => setGridApi(params.api)}
              getRowId={(params) => String(params.data.MoldSizeId)}
              loading={isLoading}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="MOLD SIZE" onClick={handleNewMoldSize} />
        </Grid>
      </Grid>
      <MoldSizeModal
        moldSize={moldSize}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogMoldSizesFromAPI}
      />
    </>
  );
};
